// client/src/v2/mentor-livro/trilha.tsx
// MENTOR DO LIVRO, a tela (plano Forjamente v1, 20/10/2026). A trilha que o
// Mentor escreveu para o livro, no caderno daquele livro, SEPARADA das notas
// da pessoa. Só aparece o que o registro de leitura já liberou (o servidor
// decide: server/mentor-livro). Desligada (MENTOR_LIVRO_ATIVO) = nada aqui,
// nem um pedido à API.
//
//   - Nota: cartão curto, o texto inteiro.
//   - Análise: título + "Ler análise", que abre o texto inteiro por cima.
//   - Embaixo de cada item, 3 toques: Na hora certa / Cansou / Faltou algo.
//     Tocar no marcado desfaz. A marca muda na hora; se o servidor recusar,
//     volta ao que era.
//
// O CORPO É MARKDOWN E NUNCA VIRA HTML. O leitor abaixo entende o pouco que a
// trilha usa (parágrafo, título, lista, citação, negrito, itálico, link) e
// devolve elementos do React com o texto como TEXTO. Não há
// dangerouslySetInnerHTML: um "<script>" no corpo aparece escrito na tela.

import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../services/apiV2';
import type { TrilhaItem, TrilhaAvaliacao } from '../../services/apiV2';
import type { AuthSession } from '../../types';
import { mentorLivroAtivo, mentorLivroAtivoAgora } from './chave';

// A paleta da trilha: fundo escuro quente e a brasa da marca, em qualquer tema.
const BRASA = '#e87a3b';
const FUNDO = 'linear-gradient(180deg, #231811 0%, #1b130e 100%)';
const TINTA = '#F3ECDE';
const TINTA_2 = 'rgba(243,236,222,0.72)';
const TINTA_3 = 'rgba(243,236,222,0.5)';
// O vermelho do "tem coisa nova": só no número do caderno e no selo NOVA.
const VERMELHO = '#e5383b';
const LINHA = 'rgba(243,236,222,0.12)';
const MONO = 'Geist Mono, monospace';

/* ── markdown → React, sem HTML ─────────────────────────────────────────── */

const INLINE = /(\*\*[^*\n]+\*\*|__[^_\n]+__|\*[^*\s][^*\n]*\*|_[^_\s][^_\n]*_|`[^`\n]+`|\[[^\]\n]+\]\((?:[^()\s]|\([^()\s]*\))+\))/g;

export function inline(t: string, k = 'i', fundo = 0): React.ReactNode[] {
  if (fundo > 3) return [t];
  const out: React.ReactNode[] = [];
  let ultimo = 0; let m: RegExpExecArray | null; let n = 0;
  const re = new RegExp(INLINE.source, 'g');
  while ((m = re.exec(t)) !== null) {
    if (m.index > ultimo) out.push(t.slice(ultimo, m.index));
    const b = m[0]; const key = `${k}.${n++}`;
    if (b.startsWith('**') || b.startsWith('__')) out.push(<strong key={key}>{inline(b.slice(2, -2), key, fundo + 1)}</strong>);
    else if (b.startsWith('`')) out.push(<code key={key} style={{ fontFamily: MONO, fontSize: '0.9em' }}>{b.slice(1, -1)}</code>);
    else if (b.startsWith('[')) {
      const lm = b.match(/^\[([^\]]+)\]\(((?:[^()\s]|\([^()\s]*\))+)\)$/);
      const href = lm && /^https?:\/\//i.test(lm[2]) ? lm[2] : null;
      out.push(href
        ? <a key={key} href={href} target="_blank" rel="noopener noreferrer" style={{ color: BRASA }}>{lm![1]}</a>
        : <span key={key}>{lm ? lm[1] : b}</span>);
    } else out.push(<em key={key}>{inline(b.slice(1, -1), key, fundo + 1)}</em>);
    ultimo = m.index + b.length;
  }
  if (ultimo < t.length) out.push(t.slice(ultimo));
  return out;
}

export type Bloco = { t: 'p' | 'h' | 'hh' | 'q' | 'ul' | 'ol' | 'hr'; linhas: string[] };

export function blocos(md: string): Bloco[] {
  const out: Bloco[] = [];
  let atual: Bloco | null = null;
  const fechar = () => { if (atual) out.push(atual); atual = null; };
  for (const bruta of String(md || '').replace(/\r\n?/g, '\n').split('\n')) {
    const l = bruta.trimEnd();
    if (!l.trim()) { fechar(); continue; }
    let m: RegExpMatchArray | null;
    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(l)) { fechar(); out.push({ t: 'hr', linhas: [] }); continue; }
    if ((m = l.match(/^\s*(#{1,6})\s+(.*)$/))) { fechar(); out.push({ t: m[1].length <= 2 ? 'h' : 'hh', linhas: [m[2]] }); continue; }
    const tipo: Bloco['t'] | null = /^\s*>/.test(l) ? 'q' : /^\s*[-*+]\s+/.test(l) ? 'ul' : /^\s*\d+[.)]\s+/.test(l) ? 'ol' : null;
    const texto = tipo === 'q' ? l.replace(/^\s*>\s?/, '') : tipo === 'ul' ? l.replace(/^\s*[-*+]\s+/, '') : tipo === 'ol' ? l.replace(/^\s*\d+[.)]\s+/, '') : l.trim();
    if (tipo) {
      if (!atual || atual.t !== tipo) { fechar(); atual = { t: tipo, linhas: [] }; }
      atual.linhas.push(texto);
    } else if (atual && (atual.t === 'p' || atual.t === 'q')) {
      // Linha seguida continua o mesmo parágrafo (ou a mesma citação).
      atual.linhas[atual.linhas.length - 1] += ' ' + texto;
    } else if (atual && (atual.t === 'ul' || atual.t === 'ol')) {
      atual.linhas[atual.linhas.length - 1] += ' ' + texto;
    } else { fechar(); atual = { t: 'p', linhas: [texto] }; }
  }
  fechar();
  return out;
}

/** O corpo da trilha (markdown) como elementos do React. Exportado para o teste. */
export function Markdown({ md, tamanho = 15 }: { md: string; tamanho?: number }) {
  const p: React.CSSProperties = { margin: '0 0 0.75em', fontSize: tamanho, lineHeight: 1.6, color: TINTA };
  return (
    <div data-trilha-md>
      {blocos(md).map((b, i) => {
        const k = 'b' + i;
        if (b.t === 'hr') return <div key={k} style={{ height: 1, background: LINHA, margin: '1.1em 0' }} />;
        if (b.t === 'h') return <h3 key={k} className="serif" style={{ margin: '1em 0 0.4em', fontSize: tamanho + 8, fontWeight: 400, lineHeight: 1.15, color: TINTA }}>{inline(b.linhas[0], k)}</h3>;
        if (b.t === 'hh') return <h4 key={k} style={{ margin: '1em 0 0.35em', fontFamily: MONO, fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 500, color: BRASA }}>{inline(b.linhas[0], k)}</h4>;
        if (b.t === 'q') return <blockquote key={k} style={{ margin: '0 0 0.85em', padding: '2px 0 2px 14px', borderLeft: `2px solid ${BRASA}`, fontStyle: 'italic', color: TINTA_2, fontSize: tamanho, lineHeight: 1.6 }}>{b.linhas.map((l, j) => <div key={j}>{inline(l, `${k}.${j}`)}</div>)}</blockquote>;
        if (b.t === 'ul' || b.t === 'ol') {
          const Lista = b.t === 'ul' ? 'ul' : 'ol';
          return <Lista key={k} style={{ margin: '0 0 0.85em', paddingLeft: 20, color: TINTA, fontSize: tamanho, lineHeight: 1.6 }}>{b.linhas.map((l, j) => <li key={j} style={{ marginBottom: 4 }}>{inline(l, `${k}.${j}`)}</li>)}</Lista>;
        }
        return <p key={k} style={p}>{inline(b.linhas.join(' '), k)}</p>;
      })}
    </div>
  );
}

/* ── avaliação de 1 toque ───────────────────────────────────────────────── */

export const OPCOES: { v: TrilhaAvaliacao; rotulo: string }[] = [
  { v: 'na_hora', rotulo: 'Na hora certa' },
  { v: 'cansou', rotulo: 'Cansou' },
  { v: 'faltou', rotulo: 'Faltou algo' },
];

function Avaliar({ item, onAvaliar, erro }: { item: TrilhaItem; onAvaliar: (v: TrilhaAvaliacao | null) => void; erro: boolean }) {
  return (
    <div data-trilha-avaliar={item.id}>
      <div role="group" aria-label="Como esta nota chegou para você?" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
        {OPCOES.map((o) => {
          const marcado = item.avaliacao === o.v;
          return (
            <button key={o.v} type="button" aria-pressed={marcado} data-avaliacao={o.v}
              onClick={() => onAvaliar(marcado ? null : o.v)}
              style={{
                height: 30, padding: '0 12px', borderRadius: 999, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 12.5, fontWeight: marcado ? 600 : 500,
                border: `1px solid ${marcado ? BRASA : 'rgba(243,236,222,0.16)'}`,
                background: marcado ? 'rgba(232,122,59,0.18)' : 'transparent',
                color: marcado ? '#F6B68C' : TINTA_3,
                transition: 'background 160ms, border-color 160ms, color 160ms',
              }}>
              {o.rotulo}
            </button>
          );
        })}
      </div>
      {erro && <div data-trilha-erro role="status" style={{ marginTop: 6, fontSize: 12, color: '#F08C6A' }}>Não consegui guardar — tente de novo.</div>}
    </div>
  );
}

/* ── a análise inteira, por cima ────────────────────────────────────────── */

function AnaliseAberta({ item, onFechar, avaliar }: { item: TrilhaItem; onFechar: () => void; avaliar: React.ReactNode }) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onFechar]);
  return (
    <div data-trilha-analise-aberta role="dialog" aria-modal="true" aria-label={item.titulo || 'Análise do Mentor'}
      style={{ position: 'fixed', inset: 0, zIndex: 95, background: '#1a120d', display: 'flex', flexDirection: 'column', maxWidth: 480, margin: '0 auto' }}>
      <div style={{ flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'calc(12px + env(safe-area-inset-top, 0px)) 16px 10px', borderBottom: `1px solid ${LINHA}` }}>
        <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.15em', color: BRASA }}>ANÁLISE · P. {item.pagina_gatilho}</span>
        <button type="button" onClick={onFechar} aria-label="Fechar análise" style={{ width: 36, height: 36, borderRadius: '50%', border: `1px solid ${LINHA}`, background: 'transparent', color: TINTA, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>
        </button>
      </div>
      <div className="om-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '22px 22px calc(40px + env(safe-area-inset-bottom, 0px))' }}>
        <div style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.15em', color: TINTA_3, marginBottom: 8 }}>TRILHA DO MENTOR</div>
        {item.titulo && <h2 className="serif" style={{ margin: '0 0 18px', fontSize: 31, fontWeight: 400, lineHeight: 1.08, color: TINTA }}>{item.titulo}</h2>}
        <Markdown md={item.corpo} tamanho={16} />
        <div style={{ marginTop: 22, paddingTop: 14, borderTop: `1px solid ${LINHA}` }}>
          <div style={{ fontSize: 12.5, color: TINTA_3 }}>Esta análise chegou na hora certa?</div>
          {avaliar}
        </div>
      </div>
    </div>
  );
}

/* ── a seção ────────────────────────────────────────────────────────────── */

const VISIVEIS = 3; // os mais recentes; o resto da trilha a um toque

export function TrilhaDoMentor({ session, bookId, focar, onAbrirTravessia }: { session: AuthSession | null; bookId: string; focar?: boolean; onAbrirTravessia?: () => void }) {
  const [ligado, setLigado] = useState<boolean | null>(mentorLivroAtivoAgora);
  const [dados, setDados] = useState<{ itens: TrilhaItem[]; proxima: number | null; travessia?: boolean } | null>(null);
  const [aberta, setAberta] = useState<string | null>(null);
  const [tudo, setTudo] = useState(false);
  const [erros, setErros] = useState<Record<string, boolean>>({});
  const [brilho, setBrilho] = useState(false);
  const toques = useRef<Record<string, number>>({});
  const raiz = useRef<HTMLDivElement>(null);

  useEffect(() => { let vivo = true; mentorLivroAtivo().then((v) => { if (vivo) setLigado(v); }); return () => { vivo = false; }; }, []);

  useEffect(() => {
    if (ligado !== true || !session) return;
    let vivo = true;
    setDados(null);
    api.trilhaDoLivro(session, bookId)
      .then((r) => {
        const itens: TrilhaItem[] = Array.isArray(r?.itens) ? r.itens : [];
        const travessia = !!r?.travessia && !!onAbrirTravessia;
        if (vivo) setDados({ itens, proxima: Number(r?.proxima_pagina) > 0 ? Number(r.proxima_pagina) : null, travessia });
        // Com a Travessia, a trilha mora no mapa: quem marca como visto é ele.
        if (travessia) return;
        // Abriu a trilha: o que era novo foi visto. O selo "nova" fica na tela
        // até sair dela; o número vermelho do caderno some (avisarTrilhaVista).
        if (itens.some((i) => !i.visto_em)) {
          api.marcarTrilhaVista(session, bookId).then(() => avisarTrilhaVista(bookId)).catch(() => {});
        }
      })
      .catch(() => { if (vivo) setDados({ itens: [], proxima: null }); });
    return () => { vivo = false; };
  }, [ligado, session, bookId]);

  // Veio do aviso "nova nota do Mentor": rola até aqui e acende a seção.
  useEffect(() => {
    if (!focar || !dados || !dados.itens.length || !raiz.current) return;
    const t = setTimeout(() => {
      raiz.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      setBrilho(true);
      setTimeout(() => setBrilho(false), 1800);
    }, 120);
    return () => clearTimeout(t);
  }, [focar, dados]);

  if (ligado !== true || !dados) return null;

  // Quem tem a Travessia: aqui fica só o atalho para o mapa.
  if (dados.travessia && onAbrirTravessia) {
    const novas = dados.itens.filter((i) => !i.visto_em).length;
    return (
      <button type="button" data-trilha-mentor data-trilha-atalho onClick={onAbrirTravessia}
        style={{ margin: '14px 20px 0', width: 'calc(100% - 40px)', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 14, border: '1px solid var(--line-strong)', background: 'var(--paper-2)', color: 'var(--ink)', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}>
        <span aria-hidden style={{ width: 34, height: 34, borderRadius: '50%', flex: 'none', display: 'grid', placeItems: 'center', background: 'var(--ember-soft)', color: BRASA }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 20c3-1 4-5 7-6s5 2 8-1 2-7 3-9" /><circle cx="21" cy="4" r="1.2" fill="currentColor" /></svg>
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontFamily: 'var(--font-serif)', fontSize: 18 }}>A Travessia deste livro</span>
          <span style={{ display: 'block', fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2 }}>
            {novas > 0 ? `${novas === 1 ? 'Um trecho novo do caminho abriu' : `${novas} trechos novos do caminho abriram`}.` : 'As notas do Mentor agora moram no mapa.'}
          </span>
        </span>
        {novas > 0 && <span style={{ position: 'relative', width: 20, height: 20, flex: 'none' }}><BadgeMentor n={novas} style={{ top: 0, right: 0 }} /></span>}
      </button>
    );
  }

  if (!dados.itens.length) {
    if (!dados.proxima) return null;
    return (
      <div data-trilha-mentor data-trilha-vazia style={{ margin: '14px 20px 0', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--ink-4)' }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: BRASA, flex: 'none' }} />
        A primeira nota do Mentor chega na página {dados.proxima}.
      </div>
    );
  }

  const avaliar = (item: TrilhaItem, v: TrilhaAvaliacao | null) => {
    const antes = item.avaliacao;
    const n = (toques.current[item.id] = (toques.current[item.id] || 0) + 1);
    const trocar = (a: TrilhaAvaliacao | null) => setDados((d) => d && ({ ...d, itens: d.itens.map((i) => (i.id === item.id ? { ...i, avaliacao: a } : i)) }));
    trocar(v);
    setErros((e) => ({ ...e, [item.id]: false }));
    api.avaliarTrilha(session, item.id, v).catch(() => {
      // Só volta se ninguém tocou de novo neste item depois deste toque.
      if (toques.current[item.id] !== n) return;
      trocar(antes);
      setErros((e) => ({ ...e, [item.id]: true }));
      setTimeout(() => setErros((e) => ({ ...e, [item.id]: false })), 4000);
    });
  };

  const itens = dados.itens;
  const escondidos = tudo ? 0 : Math.max(0, itens.length - VISIVEIS);
  const mostrar = itens.slice(escondidos);
  const ultimaPagina = itens[itens.length - 1].pagina_gatilho;
  const itemAberto = aberta ? itens.find((i) => i.id === aberta) : null;

  return (
    <section ref={raiz} id="trilha-do-mentor" data-trilha-mentor aria-label="Trilha do Mentor"
      style={{
        margin: '16px 16px 0', padding: '18px 16px 16px', borderRadius: 18, background: FUNDO,
        border: `1px solid ${brilho ? 'rgba(232,122,59,0.7)' : 'rgba(232,122,59,0.22)'}`,
        boxShadow: brilho ? '0 0 0 4px rgba(232,122,59,0.16)' : '0 10px 28px -14px rgba(0,0,0,0.6)',
        transition: 'border-color 400ms, box-shadow 400ms', scrollMarginTop: 12,
      }}>
      <div style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.16em', color: BRASA, marginBottom: 6 }}>MENTOR DO LIVRO</div>
      <h2 className="serif" style={{ margin: 0, fontSize: 26, fontWeight: 400, lineHeight: 1.05, color: TINTA }}>Trilha do Mentor</h2>
      <div style={{ fontSize: 12.5, color: TINTA_3, marginTop: 6 }}>
        {itens.length === 1 ? '1 item liberado' : `${itens.length} itens liberados`} até a p. {ultimaPagina}, conforme você lê.
      </div>

      {escondidos > 0 && (
        <button type="button" data-trilha-ver-tudo onClick={() => setTudo(true)} style={{ marginTop: 12, padding: 0, border: 'none', background: 'transparent', color: TINTA_2, fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}>
          Ver {escondidos === 1 ? 'o item anterior' : `os ${escondidos} itens anteriores`}
        </button>
      )}

      <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {mostrar.map((item, idx) => {
          const novaPagina = idx === 0 || mostrar[idx - 1].pagina_gatilho !== item.pagina_gatilho;
          const av = <Avaliar item={item} erro={!!erros[item.id]} onAvaliar={(v) => avaliar(item, v)} />;
          return (
            <div key={item.id} data-trilha-item={item.id} data-tipo={item.tipo}>
              {(novaPagina || !item.visto_em) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: MONO, fontSize: 10, letterSpacing: '0.14em', color: TINTA_3, marginBottom: 7 }}>
                  {novaPagina && <span>P. {item.pagina_gatilho}</span>}
                  {!item.visto_em && <span data-trilha-nova style={{ padding: '2px 7px', borderRadius: 999, background: VERMELHO, color: '#fff', fontSize: 9, fontWeight: 700, letterSpacing: '0.12em' }}>NOVA</span>}
                </div>
              )}
              {item.tipo === 'analise' ? (
                <div style={{ padding: '14px 14px 12px', borderRadius: 14, background: 'rgba(232,122,59,0.07)', border: '1px solid rgba(232,122,59,0.25)' }}>
                  <div style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.15em', color: BRASA, marginBottom: 6 }}>ANÁLISE</div>
                  <div className="serif" style={{ fontSize: 21, lineHeight: 1.12, color: TINTA }}>{item.titulo || 'Análise do Mentor'}</div>
                  <button type="button" data-trilha-ler onClick={() => setAberta(item.id)} style={{ marginTop: 10, padding: 0, border: 'none', background: 'transparent', color: BRASA, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    Ler análise
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="13 6 19 12 13 18" /></svg>
                  </button>
                  {av}
                </div>
              ) : (
                <div style={{ padding: '12px 14px 12px', borderRadius: 14, background: 'rgba(243,236,222,0.04)', borderLeft: `2px solid ${BRASA}` }}>
                  {item.titulo && <div className="serif" style={{ fontSize: 18, lineHeight: 1.15, color: TINTA, marginBottom: 6 }}>{item.titulo}</div>}
                  <Markdown md={item.corpo} tamanho={14.5} />
                  {av}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {itemAberto && (
        <AnaliseAberta item={itemAberto} onFechar={() => setAberta(null)}
          avaliar={<Avaliar item={itemAberto} erro={!!erros[itemAberto.id]} onAvaliar={(v) => avaliar(itemAberto, v)} />} />
      )}
    </section>
  );
}

/* ── o aviso depois do registro ─────────────────────────────────────────── */

export const textoAvisoTrilha = (n: number) => (n === 1 ? '1 nova nota do Mentor liberada' : `${n} novas notas do Mentor liberadas`);

export function AvisoTrilha({ n, onAbrir, onFechar }: { n: number; onAbrir: () => void; onFechar: () => void }) {
  return (
    <div data-aviso-trilha role="status" style={{
      position: 'fixed', left: 0, right: 0, zIndex: 60, display: 'flex', justifyContent: 'center', pointerEvents: 'none',
      bottom: 'calc(max(14px, env(safe-area-inset-bottom, 14px)) + 78px)', padding: '0 16px',
    }}>
      <div style={{ pointerEvents: 'auto', display: 'flex', alignItems: 'center', maxWidth: 400, borderRadius: 999, background: '#1d140e', border: '1px solid rgba(232,122,59,0.4)', boxShadow: '0 12px 30px -10px rgba(0,0,0,0.55)', animation: 'avTrilha 260ms ease-out' }}>
        <style>{'@keyframes avTrilha{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}'}</style>
        <button type="button" data-aviso-trilha-abrir onClick={onAbrir} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 6px 10px 14px', border: 'none', background: 'transparent', color: TINTA, fontFamily: 'inherit', fontSize: 13.5, cursor: 'pointer' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: BRASA, boxShadow: '0 0 0 3px rgba(232,122,59,0.2)', flex: 'none' }} />
          <span>{textoAvisoTrilha(n)}</span>
          <span style={{ color: BRASA, fontWeight: 600 }}>Ver</span>
        </button>
        <button type="button" onClick={onFechar} aria-label="Dispensar aviso" style={{ width: 34, height: 34, marginRight: 4, border: 'none', background: 'transparent', color: TINTA_3, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>
        </button>
      </div>
    </div>
  );
}

/* ── o número vermelho e o convite depois do registro ───────────────────── */

// A trilha foi aberta e marcada como vista: quem mostra o número (o App)
// pergunta de novo ao servidor.
export const EVENTO_TRILHA_VISTA = 'forjamente:trilha-vista';
export const avisarTrilhaVista = (bookId: string) => {
  try { window.dispatchEvent(new CustomEvent(EVENTO_TRILHA_VISTA, { detail: { bookId } })); } catch { /* sem window */ }
};

/** Bolinha vermelha pulsando com quantas notas do Mentor estão liberadas e
 *  não abertas. Vai dentro de um pai com position: relative (o botão do caderno). */
export function BadgeMentor({ n, style }: { n: number; style?: React.CSSProperties }) {
  if (!(n > 0)) return null;
  return (
    <span data-badge-mentor={n} aria-label={n === 1 ? '1 nota nova do Mentor' : `${n} notas novas do Mentor`} style={{
      position: 'absolute', top: -6, right: -7, minWidth: 19, height: 19, padding: '0 5px', boxSizing: 'border-box',
      borderRadius: 999, background: VERMELHO, color: '#fff', fontSize: 11, fontWeight: 700, lineHeight: '19px', textAlign: 'center',
      fontFamily: 'Geist, Geist Fallback, -apple-system, system-ui, sans-serif', pointerEvents: 'none',
      animation: 'badgeMentor 1.4s ease-in-out infinite', ...style,
    }}>
      <style>{'@keyframes badgeMentor{0%,100%{transform:scale(1);box-shadow:0 0 0 2px var(--paper,#161210),0 0 0 0 rgba(229,56,59,0.6)}50%{transform:scale(1.14);box-shadow:0 0 0 2px var(--paper,#161210),0 0 0 8px rgba(229,56,59,0)}}@media (prefers-reduced-motion: reduce){[data-badge-mentor]{animation:none!important}}'}</style>
      {n > 9 ? '9+' : n}
    </span>
  );
}

/** No fim do registro, quando ele liberou notas: no lugar do "Anote sobre o
 *  que você leu", o convite para ler o que o Mentor deixou. */
export function ConviteTrilha({ n, onLer, onDepois, travessia = false }: { n: number; onLer: () => void; onDepois: () => void; travessia?: boolean }) {
  return (
    <div data-convite-trilha style={{ padding: '8px 4px 6px', textAlign: 'center' }}>
      <div style={{ position: 'relative', width: 56, height: 56, margin: '6px auto 16px', borderRadius: '50%', background: 'var(--ember-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--ember)" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H11v16H5.5A1.5 1.5 0 0 1 4 18.5z"/><path d="M20 5.5A1.5 1.5 0 0 0 18.5 4H13v16h5.5A1.5 1.5 0 0 0 20 18.5z"/></svg>
        <BadgeMentor n={n} />
      </div>
      <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.16em', color: 'var(--ember)', marginBottom: 6 }}>{travessia ? 'A TRAVESSIA' : 'MENTOR DO LIVRO'}</div>
      <div className="serif" style={{ fontSize: 23, lineHeight: 1.15, color: 'var(--ink)', maxWidth: 300, margin: '0 auto 8px' }}>
        {travessia ? 'A névoa recuou' : n === 1 ? 'Nota do Mentor liberada' : `${n} notas do Mentor liberadas`}
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ink-3)', maxWidth: 290, margin: '0 auto 22px' }}>
        {travessia
          ? (n === 1 ? 'Uma parada nova apareceu no caminho, escrita para as páginas que você acabou de ler.' : `${n} paradas novas apareceram no caminho, escritas para as páginas que você acabou de ler.`)
          : n === 1 ? 'Escrita para as páginas que você acabou de ler.' : 'Escritas para as páginas que você acabou de ler.'}
      </div>
      <button type="button" data-convite-trilha-ler onClick={onLer} style={{ width: '100%', padding: 15, borderRadius: 14, border: 'none', background: 'var(--ember)', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>{travessia ? 'Ver o caminho' : 'Ler agora'}</button>
      <button type="button" data-convite-trilha-depois onClick={onDepois} style={{ width: '100%', marginTop: 10, padding: 12, borderRadius: 12, border: 'none', background: 'transparent', color: 'var(--ink-3)', fontSize: 14, cursor: 'pointer' }}>Depois</button>
    </div>
  );
}
